import React, { useState, useEffect, useRef } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements
} from '@stripe/react-stripe-js';
import { loadStripe, PaymentIntentResult, SetupIntentResult } from '@stripe/stripe-js';
import axios from 'axios';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/common/Footer';
import useMediaQuery from '@mui/material/useMediaQuery';
import AccordArrow from '../../assets/icon-components/AccordArrow';
import checkIcon from '../../assets/icons/CheckTick.svg';
import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import ErrorDisplay from '../../components/BaseComponents/ErrorDisplay';
import { categorizeError, ErrorInfo } from '../../utils/errorHandler';
import { getProductType, getPageType, getCheckoutStep, getUTMs } from '../../utils/dataLayer';
import {
  CHECKOUT_ADDONS_STORAGE_KEY,
  CheckoutAddonSelection,
  clearCheckoutAddonsStorage,
  resolveCheckoutAddonsForRequest,
} from '../../utils/checkoutAddons';
import './CompleteSubscription.scss';

const plans = [
  {
    id: 'coreVitals',
    title: 'CoreVitals Membership',
    description: 'Ongoing health tracking without imaging - up to 4x blood tests/year',
    bannerImg: bannerImg1,
    list: [
      '1 blood biomarker test per year',
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations"
    ],
  },
  {
    id: 'coreVitalsPlus',
    title: 'CoreVitals+ Membership',
    description: 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.',
    bannerImg: bannerImg2,
    list: [
      'Annual full-body MRI',
      'Pre- and post-scan clinician consults',
      'Combined blood and MRI insights in-app',
      'Ongoing tracking and progress monitoring'
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Book a full body scan at one of our numerous partner scan centers across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist to review results, personalised insights, and recommendations"
    ],
  },
  {
    id: 'fullBody',
    title: 'Full-body MRI',
    description: 'A one-time internal snapshot',
    bannerImg: bannerImg3,
    list: [
      'In-app full-body scan report',
      'Doctor review + consult',
      'Pre and post scan clinician consults',
      'Report organised by body part',
      '60-90 minutes in the scanner',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations"
    ],
  },
];

type PlanType = 'coreVitals' | 'coreVitalsPlus' | 'fullBody' | '';
type PaymentType = 'monthly' | 'yearly';

interface SelectedPlanData {
  type: PlanType;
  testCount: number | null;
  product: StripeProduct | null;
  productId: string | null;
  paymentType: PaymentType;
}

const ADDON_DETAILS: Record<string, { name: string; amount: number }> = {
  AMH: { name: 'AMH', amount: 75 },
  CEA: { name: 'CEA', amount: 50 },
  CA19_9: { name: 'CA19-9', amount: 45 },
  BNP: { name: 'BNP', amount: 95 },
};

const normalizeAddonCode = (code: string): string => code.toUpperCase().replace(/-/g, '_');

const formatAddonSelectionLabel = (addon: CheckoutAddonSelection): string => {
  const normalizedCode = normalizeAddonCode(addon.code);
  const displayCode = ADDON_DETAILS[normalizedCode]?.name || normalizedCode.replace(/_/g, '-');
  const suffix = addon.selection_type === 'one_time' ? 'once-off test' : 'twice a year';
  return `${displayCode}, ${suffix}`;
};

const formatCurrencyAmount = (amount: number, currency?: string | null): string => {
  const normalizedCurrency = (currency || 'GBP').toUpperCase();
  const symbol = normalizedCurrency === 'GBP' ? '£' : normalizedCurrency === 'USD' ? '$' : normalizedCurrency === 'EUR' ? 'EUR ' : `${normalizedCurrency} `;
  return `${symbol}${amount.toFixed(2)}`;
};

type AddonBreakdownItem = {
  code: string;
  amount: number;
};

const PlanOrderSummary = ({
  selectedPlanId,
  paymentType,
  selectedTestCount,
  getPlanPrice,
  productName,
  numberOfTests,
  selectedAddonLabels = [],
  addonBreakdown = [],
  addonCurrency,
  totalDisplay,
  renewalText,
}: {
  selectedPlanId: string;
  paymentType: PaymentType;
  selectedTestCount: number | null;
  getPlanPrice: (planId: PlanType) => React.ReactNode;
  productName?: string | null;
  numberOfTests?: number | null;
  selectedAddonLabels?: string[];
  addonBreakdown?: AddonBreakdownItem[];
  addonCurrency?: string | null;
  totalDisplay?: React.ReactNode;
  renewalText?: string | null;
}) => {
  const [showWhatIncludes, setShowWhatIncludes] = useState<boolean>(false);
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const isMobile = useMediaQuery('(max-width: 767px)');
  if (!selectedPlan) {
    return null;
  }

  // Build display title with number of tests for core plans
  let displayTitle = productName || selectedPlan.title;
  if ((selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus') && numberOfTests) {
    displayTitle = `${selectedPlan.title} ${numberOfTests} test${numberOfTests > 1 ? 's' : ''}`;
  }

  let itemsToShow = selectedPlan.list;
  if (selectedPlan.id === 'coreVitalsPlus') {
    const coreVitalsPlan = plans.find(plan => plan.id === 'coreVitals');
    itemsToShow = [...(coreVitalsPlan?.list || []), ...selectedPlan.list];
  }
  
  // Dynamically update the number of tests in the list
  const testCount = numberOfTests || selectedTestCount;
  if (testCount && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${testCount} blood biomarker test${testCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }

  return (
    <div className={`inner-card plan-info ${showWhatIncludes ? 'active' : ''}`}>
      <h4 onClick={() => setShowWhatIncludes(!showWhatIncludes)} className='inner-card-title d-flex justify-content-between align-items-center'>
        Order summary {isMobile && <AccordArrow />}
      </h4>
      {isMobile && !showWhatIncludes && <div className='order-summary-description'>{displayTitle}</div>}
      {selectedPlan && (
        <div>
          <div className='order-summary-content'>
            <img
              className='order-summary-banner-img'
              src={selectedPlan.bannerImg}
              alt={`${displayTitle} banner`}
            />
            <h3 className='order-summary-title'>{displayTitle}</h3>
            <p className='order-summary-description'>{selectedPlan.description}</p>

            <div
              onClick={() => setShowWhatIncludes(!showWhatIncludes)}
              className={`what-includes ${showWhatIncludes ? 'active' : ''} ${selectedPlan.id}`}
            >
              {!isMobile && (
                <div className='what-includes-title'>
                  <span>What's included</span>
                  <AccordArrow />
                </div>
              )}
              <div className='what-includes-content'>
                <ul className='order-summary-list'>
                  {itemsToShow.map(item => (
                    <li key={item}>
                      <img src={checkIcon} alt='check' />
                      {item}
                    </li>
                  ))}
                  {selectedAddonLabels.map((label) => (
                    <li className='selected-option font-16' key={label}>
                      <span className='add-on-price'>+</span>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {addonBreakdown.map((addon) => (
            <div className='total-price no-border' key={addon.code}>
              <div>
                {addon.code} <span className='addon-label-suffix'>(one time charge)</span>
              </div>
              <div>{formatCurrencyAmount(addon.amount, addonCurrency)}</div>
            </div>
          ))}
          <div className={`total-price ${isMobile ? '' : 'no-border'}`}>
            <div>Total</div>
            <div>{totalDisplay ?? getPlanPrice(selectedPlanId as PlanType)}</div>
          </div>
          {renewalText && (
            <div className={`selected-option pt-8 ${isMobile ? 'font-16' : 'font-18'}`}>
              {renewalText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CheckoutForm = ({
  onComplete,
  memberDetails,
  intentMode,
  submitLabel,
  stepNote,
}: {
  onComplete: (memberDetails: any) => void;
  memberDetails?: any;
  intentMode: 'payment' | 'setup';
  submitLabel?: string;
  stepNote?: string;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const [processing, setProcessing] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(true);
  const [cardholderName, setCardholderName] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('card');

  useEffect(() => {
    if (memberDetails?.first_name && memberDetails?.last_name && !cardholderName) {
      const name = `${memberDetails.first_name} ${memberDetails.last_name}`;
      setCardholderName(name);
    }
  }, [memberDetails, cardholderName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    try {
      if (intentMode === 'setup') {
        const result = (await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/complete-onboarding?token=${token}`,
          },
        })) as SetupIntentResult;

        if (result.error) {
          setError(result.error.message || 'An error occurred');
        } else if (result.setupIntent) {
          if (result.setupIntent.status === 'succeeded') {
            onComplete(null);
          } else {
            setError(`Card setup ${result.setupIntent.status}. Please try again.`);
          }
        }
      } else {
        const result = (await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/complete-onboarding?token=${token}`,
          },
        })) as PaymentIntentResult;

        if (result.error) {
          setError(result.error.message || 'An error occurred');
        } else if (result.paymentIntent) {
          if (result.paymentIntent.status === 'succeeded') {
            onComplete(null);
          } else {
            setError(`Payment ${result.paymentIntent.status}. Please try again.`);
          }
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      if (axios.isAxiosError(error) && error.response && error.response.data) {
        const errorData = error.response.data as { message?: string };
        setError(
          errorData.message ||
          'Failed to process payment. Please try again.'
        );
      } else {
        setError('Failed to process payment. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const showCardholderName = selectedPaymentMethod === 'card';

  return (
    <form onSubmit={handleSubmit}>
      {showCardholderName && (
        <div className='input-label' style={{ marginBottom: '16px' }}>
          <label className='labelStyle'>Cardholder name</label>
          <input
            type='text'
            className='form-control'
            placeholder='Full name on card'
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
          />
        </div>
      )}

      <PaymentElement
        onChange={(e) => {
          const paymentMethodType = e.value?.type || 'card';
          setSelectedPaymentMethod(paymentMethodType);
        }}
      />
      <label className='checkbox-container'>
        <input
          type='checkbox'
          checked={agreeToTerms}
          onChange={(e) => setAgreeToTerms(e.target.checked)}
        />
        I agree to
        <a
          href='https://www.corevitals.me/privacy-policy'
          target='_blank'
          rel='noreferrer'
        >
          Privacy Policy
        </a>{' '}
        and
        <a
          href='https://www.corevitals.me/member-terms-and-conditions'
          target='_blank'
          rel='noreferrer'
        >
          Terms and Conditions
        </a>
      </label>

      <button
        className='button payNowBtn'
        disabled={!stripe || processing || !agreeToTerms}
      >
        {processing ? 'Processing...' : submitLabel || 'Pay Now'}
      </button>
      {stepNote && <div className='info-message' style={{ marginTop: '8px' }}>{stepNote}</div>}
      {error && <div className='error-message'>{error}</div>}
    </form>
  );
};

const CompleteSubscription: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [, setAddonClientSecret] = useState<string | null>(null);
  const [addonInvoiceStatus, setAddonInvoiceStatus] = useState<string | null>(null);
  const [addonInvoiceAmount, setAddonInvoiceAmount] = useState<number | null>(null);
  const [addonInvoiceCurrency, setAddonInvoiceCurrency] = useState<string | null>(null);
  const [intentMode, setIntentMode] = useState<'payment' | 'setup'>('payment');
  const [memberDetails, setMemberDetails] = useState<any>({});
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanData | null>(null);
  const selectedPlanRef = useRef(selectedPlan);
  selectedPlanRef.current = selectedPlan;
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [step] = useState<'payment' | 'confirmation'>('payment');
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const [subscriptionAmount, setSubscriptionAmount] = useState<number | null>(null);
  const [selectedCheckoutAddons, setSelectedCheckoutAddons] = useState<CheckoutAddonSelection[]>([]);
  const [checkoutAddonsHydrated, setCheckoutAddonsHydrated] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [numberOfTests, setNumberOfTests] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const urlParams = new URLSearchParams(window.location.search);
  const tokenParam = urlParams.get('token');

  const fetchClientSecret = React.useCallback(async () => {
    if (!tokenParam || !selectedPlan) {
      return;
    }
    try {
      setIsLoading(true);
      const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

      let productId: string | null = null;
      if (selectedPlan.product) {
        if (selectedPlan.type !== 'fullBody') {
          productId = selectedPlan.paymentType === 'monthly'
            ? selectedPlan.product.stripe_price_monthly_id
            : selectedPlan.product.stripe_price_yearly_id;
        } else {
          productId = selectedPlan.productId || selectedPlan.product.stripe_price_yearly_id || selectedPlan.product.stripe_price_monthly_id;
        }
      } else if (selectedPlan.productId) {
        productId = selectedPlan.productId;
      }

      let promocode: string | null = null;
      const signupResponse = localStorage.getItem('signupResponse');
      if (signupResponse) {
        try {
          const signupData = JSON.parse(signupResponse);
          if (signupData.promocode?.trim()) {
            promocode = signupData.promocode.trim();
          }
        } catch (error) {
          // Error parsing signup response
        }
      }
      if (!promocode) {
        const storedPromocode = localStorage.getItem('appliedPromocode');
        if (storedPromocode?.trim()) {
          promocode = storedPromocode.trim();
        }
      }

      const payload: any = {};
      if (promocode?.trim()) {
        payload.promocode = promocode.trim();
      }
      if (productId) {
        payload.product_id = productId;
      }
      if (selectedCheckoutAddons.length > 0) {
        payload.addons = selectedCheckoutAddons;
      }
      const testsPerYear = selectedPlan?.testCount;
      if (typeof testsPerYear === 'number' && testsPerYear > 0) {
        payload.blood_tests_per_year = testsPerYear;
      }

      const response = await axios.post(
        `${baseURL}/${API_V1_PREFIX}subscriptions`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${tokenParam}`
          }
        }
      );

      if (response.data.client_secret) {
        setClientSecret(response.data.client_secret);
        setIntentMode('payment');
      } else if (response.data.setup_intent_client_secret) {
        setClientSecret(response.data.setup_intent_client_secret);
        setIntentMode('setup');
      } else {
        setClientSecret(null);
      }
      if (response.data.addon_invoice_client_secret) {
        setAddonClientSecret(response.data.addon_invoice_client_secret);
      } else {
        setAddonClientSecret(null);
      }
      setAddonInvoiceStatus(response.data.addon_invoice_status || null);
      setAddonInvoiceAmount(
        response.data.addon_invoice_amount !== undefined && response.data.addon_invoice_amount !== null
          ? Number(response.data.addon_invoice_amount)
          : null
      );
      setAddonInvoiceCurrency(response.data.addon_invoice_currency || null);
      if (response.data.amount !== undefined && response.data.amount !== null) {
        setSubscriptionAmount(response.data.amount);
      }
      setErrorInfo(null);
    } catch (error) {
      const categorizedError = categorizeError(error);
      if (
        categorizedError.type === 'access_restricted' ||
        categorizedError.type === 'network_error' ||
        categorizedError.type === 'session_expired'
      ) {
        setErrorInfo(categorizedError);
      } else {
        setErrorInfo(null);
        if ((error as any)?.response?.data?.detail) {
          toast.error((error as any)?.response?.data?.detail);
        } else if ((error as any)?.response?.data?.message) {
          toast.error((error as any)?.response?.data?.message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [tokenParam, selectedPlan, selectedCheckoutAddons]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getStripeProducts();
        const activeProducts = response.products.filter((product: StripeProduct) => product.active);
        setProducts(activeProducts);
      } catch (err: any) {
        console.error('Error fetching products:', err);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      try {
        setSelectedPlan(JSON.parse(storedPlan));
      } catch (error) {
        console.error('Error parsing selected plan:', error);
      }
    }
  }, []);

  useEffect(() => {
    setCheckoutAddonsHydrated(false);
    const addons = resolveCheckoutAddonsForRequest(location.state) || [];
    setSelectedCheckoutAddons(addons);

    // `location.state` is lost on refresh. Persist any explicitly provided checkout add-ons
    // so the payment page can rehydrate them after a hard reload.
    const state = location.state as unknown;
    if (state && typeof state === 'object' && 'checkoutAddons' in (state as any)) {
      try {
        if (addons.length > 0) {
          localStorage.setItem(CHECKOUT_ADDONS_STORAGE_KEY, JSON.stringify(addons));
          sessionStorage.setItem(CHECKOUT_ADDONS_STORAGE_KEY, JSON.stringify(addons));
        } else {
          clearCheckoutAddonsStorage();
        }
      } catch {
        // ignore storage failures
      }
    }
    setCheckoutAddonsHydrated(true);
  }, [location.state]);

  useEffect(() => {
    setSubscriptionAmount(null);
  }, [selectedPlan?.type, selectedPlan?.paymentType]);

  useEffect(() => {
    if (!tokenParam) {
      navigate('/plans');
      return;
    }
    setToken(tokenParam);
  }, [tokenParam, navigate]);

  useEffect(() => {
    const signupResponse = localStorage.getItem('signupResponse');
    if (signupResponse && tokenParam) {
      try {
        const signupData = JSON.parse(signupResponse);
        const amount = signupData.amount;
        // If discount makes total free, backend may return a SetupIntent client_secret instead of PaymentIntent.
        if ((amount === 0 || amount === '0') && !signupData.client_secret && !signupData.setup_intent_client_secret) {
          localStorage.removeItem('signupResponse');
          navigate(`/complete-onboarding?token=${tokenParam}`);
          return;
        }

        if ((signupData.client_secret || signupData.setup_intent_client_secret) && amount != null) {
          const path = window.location.pathname;
          // Get membership_type from product plan_type, or derive from selectedPlan type
          const membershipType = selectedPlan?.product?.plan_type || 
            (selectedPlan?.type === 'coreVitals' ? 'CORE' :
             selectedPlan?.type === 'coreVitalsPlus' ? 'PLUS' :
             selectedPlan?.type === 'fullBody' ? 'SCAN' : null);
          
          window.dataLayer.push({
            event: 'signup',
            url: window.location.href,
            host: window.location.host,
            path,
            product_type: getProductType(path, membershipType),
            page_type: getPageType(path),
            checkout_step: getCheckoutStep(path),
            ...getUTMs(),
            referrer: document.referrer || null,
            // Preserve existing fields
            interval: selectedPlan?.paymentType === 'monthly' ? 'month' : 'year',
            amount: selectedPlan?.paymentType === 'monthly' ? signupData.amount * 12 : signupData.amount,
            member_details: {
              email: signupData.email,
              first_name: signupData.first_name,
              last_name: signupData.last_name,
              phone: signupData.phone,
            },
            membership_type: membershipType
          });
          if (signupData.client_secret) {
            setClientSecret(signupData.client_secret);
            setIntentMode('payment');
          } else if (signupData.setup_intent_client_secret) {
            setClientSecret(signupData.setup_intent_client_secret);
            setIntentMode('setup');
          }
          // Set subscription amount from signup response
          if (amount !== undefined && amount !== null) {
            setSubscriptionAmount(amount);
          }
          localStorage.removeItem('signupResponse');
        }
      } catch (error) {
        localStorage.removeItem('signupResponse');
      }
    }
    if (tokenParam && selectedPlan && checkoutAddonsHydrated) {
      fetchClientSecret();
    }
  }, [tokenParam, selectedPlan, selectedCheckoutAddons, checkoutAddonsHydrated, location.key, fetchClientSecret, navigate]);

  useEffect(() => {
    const checkActiveSubscription = async () => {
      if (!tokenParam) return;
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}subscriptions/check-active`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`
            }
          }
        );

        setHasActiveSubscription(response?.data?.response);
      } catch (error) {
        setHasActiveSubscription(false);
      }
    };

    checkActiveSubscription();
  }, [tokenParam]);

  // Map membership_type from API to plan ID
  const mapMembershipTypeToPlanId = (membershipType: string | null | undefined): PlanType => {
    if (!membershipType) return '';

    const type = membershipType.toLowerCase();
    if (type === 'scan') return 'fullBody';
    if (type === 'core') return 'coreVitals';
    if (type === 'plus') return 'coreVitalsPlus';

    return '';
  };

  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!tokenParam) return;
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}members/?load_strategy=with_subscription`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`
            }
          }
        );
        const memberData = response.data;
        const path = window.location.pathname;
        const membershipType = memberData?.membership_type;
        window.dataLayer.push({
          event: 'subscription_page_view',
          url: window.location.href,
          host: window.location.host,
          path,
          product_type: getProductType(path, membershipType),
          page_type: getPageType(path),
          checkout_step: getCheckoutStep(path),
          ...getUTMs(),
          referrer: document.referrer || null,
          member_details: memberData,
          membership_type: membershipType
        });
        setMemberDetails(memberData);

        // Set number of tests if available
        if (memberData.number_of_blood_test_plan) {
          setNumberOfTests(memberData.number_of_blood_test_plan);
        }

        // If no selected plan from localStorage, set it based on membership_type
        if (!selectedPlanRef.current && memberData.membership_type) {
          const planId = mapMembershipTypeToPlanId(memberData.membership_type);
          if (planId) {
            // Find matching product
            const matchingProduct = products.find((p: StripeProduct) => {
              if (planId === 'fullBody') {
                return p.key === 'full_body_mri';
              }
              const productName = p.name?.toLowerCase() || '';
              return productName.includes(planId === 'coreVitals' ? 'core' :
                planId === 'coreVitalsPlus' ? 'plus' :
                  'scan');
            });

            setSelectedPlan((prev) => {
              if (prev) return prev;
              // For fullBody, use yearly payment type (one-time payment)
              const isFullBody = planId === 'fullBody';
              return {
                type: planId,
                testCount: memberData.number_of_blood_test_plan || null,
                product: matchingProduct || null,
                productId: isFullBody 
                  ? (matchingProduct?.stripe_price_yearly_id || matchingProduct?.stripe_price_monthly_id || null)
                  : (matchingProduct?.stripe_price_monthly_id || null),
                paymentType: isFullBody ? 'yearly' : 'monthly'
              };
            });
          }
        }

        setErrorInfo(null);
      } catch (error) {
        console.error('Error fetching member details:', error);
        const categorizedError = categorizeError(error);
        if (
          categorizedError.type === 'access_restricted' ||
          categorizedError.type === 'network_error' ||
          categorizedError.type === 'session_expired'
        ) {
          setErrorInfo(categorizedError);
        } else {
          setErrorInfo(null);
        }
      }
    };
    fetchMemberDetails();
  }, [tokenParam, products]);

  const stripePromise = React.useMemo(
    () => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''),
    []
  );

  const options = React.useMemo(
    () => clientSecret
      ? {
        clientSecret,
        appearance: {
          theme: 'stripe' as const,
          variables: {
            colorPrimary: '#163b21',
            colorBackground: '#ffffff',
            colorText: '#0C2012',
            colorDanger: '#dc2626',
            fontFamily: 'Satoshi, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
          },
        },
      }
      : undefined,
    [clientSecret]
  );

  const getPlanPrice = (planId: PlanType): React.ReactNode => {
    if (subscriptionAmount !== null && subscriptionAmount !== undefined) {
      const formattedAmount =
        subscriptionAmount % 1 === 0
          ? Math.round(subscriptionAmount).toLocaleString()
          : subscriptionAmount.toFixed(2);

      // For fullBody (scan) plan, don't show /month or /year
      if (planId === 'fullBody') {
        return <>£{formattedAmount}</>;
      }

      if (selectedPlan?.paymentType === 'yearly') {
        return (
          <>
            £{formattedAmount}
            <span>/year</span>
          </>
        );
      }

      return (
        <>
          £{formattedAmount}
          <span>/month</span>
        </>
      );
    }

    return <span style={{ opacity: 0.5 }}>Loading...</span>;
  };

  const formatAddonAmount = (): string => {
    if (addonInvoiceAmount === null || addonInvoiceAmount === undefined) {
      return 'Amount unavailable';
    }
    return formatCurrencyAmount(addonInvoiceAmount, addonInvoiceCurrency);
  };

  const oneTimeSelectedAddonTotal = selectedCheckoutAddons.reduce((sum, addon) => {
    if (addon.selection_type !== 'one_time') return sum;
    const amount = ADDON_DETAILS[normalizeAddonCode(addon.code)]?.amount || 0;
    return sum + amount;
  }, 0);

  const oneTimeAddonBreakdown = selectedCheckoutAddons
    .filter((addon) => addon.selection_type === 'one_time')
    .map((addon) => {
      const normalizedCode = normalizeAddonCode(addon.code);
      const details = ADDON_DETAILS[normalizedCode];
      return {
        code: details?.name || normalizedCode.replace(/_/g, '-'),
        amount: details?.amount || 0,
      };
    });

  const selectedAddonLabels = selectedCheckoutAddons.map(formatAddonSelectionLabel);

  const getCheckoutTotalDisplay = (planId: PlanType): React.ReactNode => {
    if (subscriptionAmount === null || subscriptionAmount === undefined) {
      return getPlanPrice(planId);
    }
    const total = subscriptionAmount + oneTimeSelectedAddonTotal;
    const formattedTotal =
      total % 1 === 0 ? Math.round(total).toLocaleString() : total.toFixed(2);
    if (oneTimeSelectedAddonTotal > 0) {
      return <>£{formattedTotal}</>;
    }
    return getPlanPrice(planId);
  };

  const renewalText = React.useMemo(() => {
    if (!selectedPlan || selectedPlan.type === 'fullBody') {
      return null;
    }

    const isYearly = selectedPlan.paymentType === 'yearly';
    const renewalDate = new Date();
    renewalDate.setMonth(renewalDate.getMonth() + (isYearly ? 12 : 1));
    const formattedDate = renewalDate.toLocaleDateString('en-GB');

    const catalogPlanRaw = isYearly
      ? selectedPlan.product?.yearly_amount
      : selectedPlan.product?.monthly_amount;
    const catalogPlan =
      catalogPlanRaw !== null && catalogPlanRaw !== undefined && !Number.isNaN(Number(catalogPlanRaw))
        ? Number(catalogPlanRaw)
        : null;

    const billingFrequency =
      selectedPlan.testCount && selectedPlan.testCount > 0 ? selectedPlan.testCount : 1;
    const recurringAddonTotal = selectedCheckoutAddons.reduce((sum, addon) => {
      if (addon.selection_type !== 'recurring') return sum;
      const perTest = ADDON_DETAILS[normalizeAddonCode(addon.code)]?.amount || 0;
      const recurringYearly = perTest * billingFrequency;
      return sum + (isYearly ? recurringYearly : recurringYearly / 12);
    }, 0);

    const renewalBase =
      catalogPlan !== null ? catalogPlan : subscriptionAmount ?? null;
    const renewalTotal =
      renewalBase !== null && renewalBase !== undefined
        ? renewalBase + recurringAddonTotal
        : null;

    const formattedAmount =
      renewalTotal !== null && renewalTotal !== undefined
        ? `£${renewalTotal % 1 === 0 ? Math.round(renewalTotal).toLocaleString() : renewalTotal.toFixed(2)}`
        : isYearly
          ? '£xxx'
          : '£xx';

    return `Subscription renews at ${formattedAmount}/${isYearly ? 'year' : 'month'} on ${formattedDate}`;
  }, [selectedPlan, subscriptionAmount, selectedCheckoutAddons]);

  if (errorInfo && step === 'payment') {
    return (
      <>
        <div className='c_container subscription-page-wrapper' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 100px)', width: '100%' }}>
          <div style={{ padding: '40px 20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ErrorDisplay errorInfo={errorInfo} />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (hasActiveSubscription === true) {
    return (
      <>
        <div className='c_container subscription-page-wrapper'>
          <div className='success-container'>
            <div className='subscription-header'>
              <h3>
                Hello, {memberDetails.first_name} {memberDetails.last_name}
              </h3>
              <h3>You are already subscribed to CoreVitals!</h3>
              <p>
                Thank you for being a member. You already have an active subscription.
              </p>
            </div>
            <div className='card-style white-card'>
              <h3 className='title-2'>Need assistance?</h3>
              <p>
                If you need any help or have questions about your subscription,
                please contact us at{' '}
                <a href='mailto:members@corevitals.me'>members@corevitals.me</a>
              </p>
            </div>
            <div className='card-style white-card'>
              <h3 className='title-2'>What's next?</h3>
              <p>
                You can continue with your health journey by completing your profile
                and booking your next blood test.
              </p>
              <button
                onClick={() => navigate(`/complete-profile?token=${token}`)}
                className='button button2'
              >
                Continue to Profile
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (isLoading && !clientSecret && step === 'payment') {
    return (
      <div className='main-wrapper'>
        <div className='loading-message'>Initializing payment...</div>
      </div>
    );
  }


  return (
    <>
      <div className='c_container subscription-page-wrapper'>
        <div className='two-colum-grid'>
          <div className='left-column'>
            <h2 className='select-plan-title'>Secure your order</h2>
            <h3 className='complete-payment-title'>Complete payment</h3>
            {isMobile && selectedPlan && (
              <>
                <PlanOrderSummary
                  selectedPlanId={selectedPlan.type}
                  paymentType={selectedPlan.paymentType}
                  selectedTestCount={selectedPlan.testCount}
                  getPlanPrice={getPlanPrice}
                  productName={selectedPlan.product?.name || null}
                  numberOfTests={numberOfTests}
                  selectedAddonLabels={selectedAddonLabels}
                  addonBreakdown={oneTimeAddonBreakdown}
                  addonCurrency={addonInvoiceCurrency}
                  totalDisplay={getCheckoutTotalDisplay(selectedPlan.type)}
                  renewalText={renewalText}
                />
              </>
            )}
            <div className='payment-form-container'>
              <div className='card-style white-card'>
                <h3 className='box-title'>Contact information</h3>
                <div className='input-label'>
                  <label className='labelStyle'>Email</label>
                  <input
                    type='email'
                    className='form-control'
                    value={memberDetails.email || ''}
                    placeholder='email@example.com'
                    disabled
                  />
                </div>
                <h3 className='box-title payment-method-title'>Payment method</h3>
                {(addonInvoiceAmount !== null && addonInvoiceAmount !== undefined) && (
                  <div className='input-label' style={{ marginBottom: '12px' }}>
                    <div className='labelStyle' style={{ color: '#4a4a4a' }}>
                      One-time add-ons:&nbsp;
                      {formatAddonAmount()}
                      {addonInvoiceStatus ? <>&nbsp;({addonInvoiceStatus})</> : null}
                    </div>
                  </div>
                )}
                {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && clientSecret ? (
                  <Elements key={clientSecret} stripe={stripePromise} options={options}>
                    <CheckoutForm
                      onComplete={() => {
                        const path = window.location.pathname;
                        const membershipType = memberDetails?.membership_type || 
                          selectedPlan?.product?.plan_type ||
                          (selectedPlan?.type === 'coreVitals' ? 'CORE' :
                           selectedPlan?.type === 'coreVitalsPlus' ? 'PLUS' :
                           selectedPlan?.type === 'fullBody' ? 'SCAN' : null);
                        
                        window.dataLayer.push({
                          event: 'purchase',
                          url: window.location.href,
                          host: window.location.host,
                          path,
                          product_type: getProductType(path, membershipType),
                          page_type: getPageType(path),
                          checkout_step: getCheckoutStep(path),
                          ...getUTMs(),
                          referrer: document.referrer || null,
                          // Preserve existing fields
                          member_details: memberDetails,
                          membership_type: membershipType,
                          amount: subscriptionAmount
                        });
                        navigate(`/complete-onboarding?token=${token}`);
                      }}
                      memberDetails={memberDetails}
                      intentMode={intentMode}
                      submitLabel={'Pay now'}
                    />
                  </Elements>
                ) : (
                  <div className='loading-message'>Loading payment form...</div>
                )}
              </div>
            </div>
          </div>
          {!isMobile && (
            <div className='right-column'>
              {selectedPlan && (
                <PlanOrderSummary
                  selectedPlanId={selectedPlan.type}
                  paymentType={selectedPlan.paymentType}
                  selectedTestCount={selectedPlan.testCount}
                  getPlanPrice={getPlanPrice}
                  productName={selectedPlan.product?.name || null}
                  numberOfTests={numberOfTests}
                  selectedAddonLabels={selectedAddonLabels}
                  addonBreakdown={oneTimeAddonBreakdown}
                  addonCurrency={addonInvoiceCurrency}
                  totalDisplay={getCheckoutTotalDisplay(selectedPlan.type)}
                  renewalText={renewalText}
                />
              )}
  
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CompleteSubscription;

